import React, {useState, useEffect, useRef} from 'react';
import './tailwind-compiled.css';
import {
    Download,
    Scale,
    Percent,
    PlusCircle,
    TrendingUp,
    Globe,
    Tag,
    RotateCcw,
    User,
    Phone,
    MessageCircle,
    RefreshCw,
    LogOut,
    Briefcase,
    Calculator,
    FileText,
    Settings
} from 'lucide-react';

import {initializeApp} from 'firebase/app';
// UPDATED: Added onSnapshot to imported firestore functions
import {
    doc,
    getDoc,
    setDoc,
    getFirestore,
    collection,
    addDoc,
    enableIndexedDbPersistence,
    onSnapshot
} from 'firebase/firestore';
import {
    signInWithPopup,
    onAuthStateChanged,
    signOut,
    getAuth,
    GoogleAuthProvider,
    setPersistence,
    browserLocalPersistence,
    indexedDBLocalPersistence
} from 'firebase/auth';

// ==========================================
// 1. FIREBASE SETUP
// ==========================================
const firebaseConfig = {
    apiKey: "AIzaSyCrOgsv99Uzqaq6XYVgOlEJ9l_eGgNsv7c",
    authDomain: "jewellerdesk.app",
    projectId: "goldestimatorreinfortech",
    storageBucket: "goldestimatorreinfortech.firebasestorage.app",
    messagingSenderId: "490552350765",
    appId: "1:490552350765:web:ca75ea8196652cdd3180a4"
};

let app: any;
let auth: any;
let googleProvider: any;
let db: any;

try {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);

    // Prioritize IndexedDB to persist login strictly across PWA and browser sessions
    setPersistence(auth, indexedDBLocalPersistence)
        .catch(() => {
            setPersistence(auth, browserLocalPersistence).catch(console.error);
        });

    googleProvider = new GoogleAuthProvider();
    db = getFirestore(app);

    // Enable Offline Database Persistence securely
    if (typeof window !== 'undefined') {
        enableIndexedDbPersistence(db).catch((err) => {
            if (err.code === 'failed-precondition') {
                console.warn('Multiple tabs open, offline persistence can only be enabled in one tab at a time.');
            } else if (err.code === 'unimplemented') {
                console.warn('The current browser does not support offline persistence.');
            }
        });
    }
} catch (error) {
    console.error("Firebase initialization failed. Please update your config.", error);
}

// ==========================================
// 2. CUSTOM LOGO COMPONENT
// ==========================================
interface JewellerDeskLogoProps {
    className?: string;
    letterColor?: string;
    diamondColor?: string;
    width?: number | string;
    height?: number | string;
}

const JewellerDeskLogo: React.FC<JewellerDeskLogoProps> = ({
                                                               className,
                                                               letterColor = "#1A1A1A",
                                                               diamondColor = "#D4AF37",
                                                               width,
                                                               height
                                                           }) => (
    <svg className={className} viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg" width={width} height={height}>
        <path fill={letterColor}
              d="M611.317261,548.000122 C611.308960,581.833313 611.714783,615.173401 611.159119,648.497498 C610.722046,674.710815 605.857727,700.197937 593.573547,723.704041 C575.248535,758.769226 546.404968,781.320557 508.468170,791.256042 C458.810150,804.261414 410.416809,800.315796 364.420868,776.306030 C338.300659,762.671326 317.831146,742.663940 300.961456,718.748291 C299.548859,716.745728 299.083954,715.054749 300.945374,712.989075 C316.114349,696.155640 331.221985,679.266907 346.366913,662.411743 C346.674866,662.069031 347.207520,661.928284 347.817871,661.593079 C351.081970,663.240234 352.535095,666.586670 354.622528,669.273682 C369.438049,688.344360 386.693848,704.526062 408.854462,714.769348 C429.428375,724.279114 451.041382,727.157532 473.181946,722.364868 C499.940765,716.572571 515.120422,698.852966 521.690002,672.868042 C524.991272,659.810547 525.412537,646.474854 525.455505,633.195251 C525.595032,590.034912 525.011353,546.871521 525.279358,503.712860 C525.360962,490.564697 524.670532,477.413666 525.337891,464.268921 C525.436951,462.316620 525.313110,460.332062 526.481018,458.048462 C530.978027,460.895599 534.042969,464.895477 537.604553,468.260986 C546.446106,476.615753 555.161499,485.113464 563.674255,493.801880 C566.923462,497.118164 569.004578,497.476227 572.428772,493.813110 C583.464966,482.006775 594.866577,470.541260 606.174622,458.990662 C607.392029,457.747101 608.291748,455.945038 611.316895,455.745087 C611.316895,486.407684 611.316895,516.953857 611.317261,548.000122 z"/>
        <path fill={diamondColor}
              d="M443.151611,357.848450 C423.870300,339.901978 404.913696,322.125244 385.753418,304.570892 C382.109039,301.231995 381.810577,299.163025 385.456635,295.458313 C406.254425,274.325592 426.819580,252.963272 447.375488,231.594040 C449.983093,228.883286 452.721954,227.611877 456.556671,227.617859 C521.370728,227.718826 586.185059,227.724289 650.999207,227.654495 C654.279907,227.650970 656.659180,228.673035 658.915405,230.970535 C680.277161,252.723068 701.683350,274.432129 723.153137,296.077972 C725.487488,298.431458 725.781128,300.044037 723.364136,302.677582 C699.938416,328.201935 676.598999,353.805939 653.318604,379.463226 C625.899048,409.682220 598.492676,439.914093 571.276978,470.316162 C567.800293,474.199921 565.939575,472.374176 563.286865,469.889221 C531.069580,439.708466 498.823334,409.558441 466.559235,379.427673 C458.889679,372.265198 451.126007,365.203491 443.151611,357.848450 M586.837646,376.435516 C595.711426,354.631683 604.585205,332.827850 613.984253,309.733337 C576.462708,309.133972 540.241272,309.323273 502.388733,309.542542 C523.537842,350.504364 543.837463,389.820831 564.137085,429.137268 C564.606323,429.040222 565.075562,428.943176 565.544800,428.846130 C572.555054,411.617310 579.565247,394.388458 586.837646,376.435516 M492.833435,335.630829 C488.967804,328.261841 484.995270,320.946014 481.290344,313.497131 C479.850922,310.603149 478.035339,309.336243 474.715485,309.365112 C459.065063,309.501404 443.412659,309.399902 427.761200,309.452637 C426.090424,309.458252 424.007904,308.754425 422.763184,311.631470 C458.953400,345.407135 495.246246,379.278595 531.539062,413.150085 C531.873108,412.884644 532.207092,412.619202 532.541138,412.353760 C519.425842,386.994659 506.310547,361.635529 492.833435,335.630829 M597.133240,410.627747 C599.474121,409.528290 600.977600,407.469543 602.641174,405.646362 C630.372620,375.253235 658.071777,344.830627 685.757812,314.396118 C686.719116,313.339386 688.138306,312.465576 687.882385,310.658905 C686.737793,309.321869 685.146912,309.584808 683.696716,309.575836 C669.036865,309.485138 654.376221,309.484009 639.717102,309.335693 C636.805603,309.306274 635.744202,310.849518 634.766479,313.248840 C623.146240,341.765808 611.357788,370.214600 599.849304,398.776276 C598.356079,402.481934 595.668640,406.044495 597.133240,410.627747 M563.957153,256.555817 C563.078979,255.788498 562.087402,255.117264 561.341919,254.237503 C557.766174,250.017746 554.684570,250.460663 550.709473,254.223526 C539.826050,264.525635 528.554810,274.417419 517.476929,284.515350 C516.298950,285.589142 514.736511,286.529358 514.844971,289.309509 C543.106750,289.309509 571.373169,289.309509 599.639648,289.309509 C599.832886,288.892883 600.026184,288.476257 600.219421,288.059631 C588.309631,277.714905 576.399841,267.370209 563.957153,256.555817 M491.681000,280.994781 C504.093140,270.906891 515.661682,259.919678 527.872192,248.372696 C525.907898,247.759918 525.149048,247.318985 524.388428,247.316025 C508.921143,247.255753 493.453644,247.243958 477.986206,247.226898 C475.046509,247.223663 473.992401,248.487350 475.254547,251.313705 C479.321381,260.420654 483.352051,269.543976 487.470764,278.627411 C488.128998,280.079102 488.816284,281.840057 491.681000,280.994781 M463.189728,290.086060 C464.934387,289.828461 466.885406,290.338348 468.682953,288.336426 C468.199036,286.895050 467.757874,285.160004 467.049408,283.542023 C463.521362,275.484558 459.968689,267.437134 456.332214,259.428192 C454.159760,254.643646 453.159760,254.359314 449.288208,258.178894 C440.069824,267.273590 431.033875,276.553467 421.950958,285.784790 C421.165527,286.583008 420.260681,287.455383 421.256805,290.106812 C434.590118,290.106812 448.428497,290.106812 463.189728,290.086060 M627.393799,273.915375 C629.558960,266.928192 631.784180,259.958862 633.869629,252.947983 C635.436401,247.680435 635.064575,247.195724 629.539368,247.182312 C616.394592,247.150406 603.249634,247.175568 590.104919,247.233383 C588.693848,247.239594 587.077576,246.755310 585.884338,248.246124 C586.530151,250.907806 588.902405,252.112381 590.711121,253.696732 C599.719971,261.587891 608.824280,269.369934 617.889099,277.197266 C623.763855,282.270050 624.547791,282.056519 627.393799,273.915375 M648.165100,289.930664 C661.157654,289.930664 674.150269,289.930664 687.142822,289.930664 C687.311646,289.339355 687.480469,288.748047 687.649292,288.156708 C676.887512,277.727386 667.049316,266.368622 655.070435,255.747711 C650.616516,266.603455 647.530701,276.718140 644.804626,286.950256 C644.288757,288.886566 645.662903,289.607239 648.165100,289.930664 z"/>
    </svg>
);

// ==========================================
// 3. TRANSLATIONS & DICTIONARY
// ==========================================
const translations = {
    en: {
        appTitle: "Jeweller Desk",
        marketRateBase: "Market Rate (24K Pure)",
        perTola24k: "Cost of 24 kt Gold",
        perGramRates: "Calculated Carat Rates",
        customerDetails: "Customer Details",
        customerName: "Customer Name",
        customerPhone: "WhatsApp Number",
        itemDetails: "Item Calculations",
        itemPurity: "Selected Purity",
        totalWeight: "Total Weight",
        grams: "Grams",
        tolas: "Tolas",
        ounces: "Ounces (oz t)",
        wastagePercent: "Wastage Percentage",
        additionalCosts: "Additional Costs",
        makingCharges: "Making Charges",
        stoneCharges: "Stones / Gems",
        otherCharges: "Other Expenses",
        whatsappRequired: "Please enter a valid WhatsApp number to continue.",
        discount: "Discount (if any)",
        estimate: "Estimate",
        ratePerBaseUnit: "Rate",
        ratePerGram: "Rate (Per Gram)",
        baseWeight: "Base Weight",
        wastage: "Wastage",
        netWeight: "Net Weight",
        estValue: "Est. Value",
        totalPrice: "Total Price",
        downloadReceipt: "Download PDF Receipt",
        processing: "Processing...",
        disclaimer: "This estimate is calculated based on",
        selected: "Selected",
        gold: "Gold",
        silver: "Silver",
        poweredBy: "Powered by Synforge",
        installPromptTitle: "Install Jeweller Desk",
        installPromptDesc: "Add to home screen to use it offline anytime!",
        installBtn: "Install Now",
        closeBtn: "Not Now",
        offlineNoticeTitle: "Offline Mode Instruction",
        offlineNotice: "You are currently offline. The receipt has been formatted for printing. Please choose 'Save as PDF' from your browser's printer list to save.",
        offlineNoticeClose: "Open Printer Menu",
        clearFields: "Clear Fields",
        selectCaratHint: "Tap on any carat card below to apply its rate immediately",
        contact: "Contact",
        whatsappGreeting: "Hello",
        whatsappBody: "Here is your estimate summary:",
        sendWhatsApp: "WhatsApp",
        dateTimeLabel: "Date & Time",
        whatsappDisclaimer: "Note: Prices are subject to change and vary based on the live market rate.",
        promoMessage: "Digitize your business with Jeweller Desk! \nGet your custom app by Synforge:",
        iosInstallTitle: "Install App on iPhone",
        iosInstallSteps: "Tap the Share icon below, then select 'Add to Home Screen'.",

        welcomeBack: "Welcome to Jeweller Desk",
        loginDesc: "Sign in to access the gold calculator and generate instant estimates.",
        continueWithGoogle: "Continue with Google",
        profileSetup: "Your Profile",
        whatsappPrompt: "Please enter your active WhatsApp number to unlock the calculator.",
        saveAndContinue: "Save & Continue",
        logout: "Logout",
        businessNameLabel: "Business / Brand Name (Optional)",
        supportMessage: "If there is any account setup issue, please connect to support and we're happy to set up your account.",
        contactSupport: "Contact Support (Powered by Synforge)",

        defaultPreferences: "Default Preferences",
        defaultCurrency: "Default Currency",
        defaultPhoneCode: "Default Country Code",
        defaultUnit: "Default Unit",
        defaultMetal: "Default Base Metal",
        pdfDisclaimer: "Note: Estimated prices are subject to change based on live market rates.",

        landingTitle: "Jeweller Desk",
        landingDesc: "The precise calculator for jewelers. Generate instant estimates and share beautiful PDF receipts with your customers.",

        continueAsGuest: "Continue as Guest",
        signInToUnlock: "Sign in to Unlock Receipts",
        guestUpsellText: "Create a free account to download PDF receipts and send estimates to your customers via WhatsApp.",
        logoutConfirm: "Are you sure you want to log out?",
        yes: "Yes, Log out",
        no: "Cancel",
        guestWatermark: "GUEST PREVIEW",
        sessionExpired: "Your account was logged in from another device. You have been securely logged out."
    },
    ur: {
        appTitle: "جیولر ڈیسک",
        marketRateBase: "مارکیٹ ریٹ (خالص 24K)",
        perTola24k: "بنیادی مارکیٹ ریٹ",
        perGramRates: "سونے کا معیار اور قیمتیں",
        customerDetails: "گاہک کی تفصیلات",
        customerName: "گاہک کا نام",
        customerPhone: "واٹس ایپ نمبر",
        itemDetails: "اشیاء کے حساب کتاب",
        itemPurity: "منتخب کردہ معیار",
        totalWeight: "کل وزن",
        grams: "گرام",
        tolas: "تولہ",
        ounces: "اونس (oz t)",
        wastagePercent: "کھوٹ یا کٹوتی (فیصد)",
        additionalCosts: "اضافی اخراجات",
        makingCharges: "مزدوری (بنانے کا خرچہ)",
        stoneCharges: "نگینے / پتھر",
        otherCharges: "دیگر اخراجات",
        whatsappRequired: "آگے بڑھنے کے لیے براہ کرم درست واٹس ایپ نمبر درج کریں۔",
        discount: "رعایت (اگر کوئی ہے)",
        estimate: "تخمینہ کی رسید",
        ratePerBaseUnit: "قیمت",
        ratePerGram: "قیمت (فی گرام)",
        baseWeight: "بنیادی وزن",
        wastage: "کھوٹ",
        netWeight: "خالص وزن",
        estValue: "متوقع قیمت",
        totalPrice: "کل رقم",
        downloadReceipt: "پی ڈی ایف رسید ڈاؤن لوڈ کریں",
        processing: "تیار ہو رہا ہے...",
        disclaimer: "یہ تخمینہ اس کیرٹ کی بنیاد پر لگایا گیا ہے:",
        selected: "منتخب",
        gold: "سونا",
        silver: "چاندی",
        poweredBy: "تیار کردہ: سن فورج",
        installPromptTitle: "ایپ انسٹال کریں",
        installPromptDesc: "بغیر انٹرنیٹ استعمال کرنے کے لیے اسکرین پر شامل کریں!",
        installBtn: "ابھی انسٹال کریں",
        closeBtn: "ابھی نہیں",
        offlineNoticeTitle: "آف لائن ہدایات",
        offlineNotice: "آپ کا انٹرنیٹ بند ہے۔ رسید پرنٹنگ کے لیے تیار کر دی گئی ہے۔ براہ کرم اپنے پرنٹر مینو سے 'Save as PDF' منتخب کریں۔",
        offlineNoticeClose: "پرنٹ مینو کھولیں",
        clearFields: "تفصیلات صاف کریں",
        selectCaratHint: "کسی بھی کیرٹ کارڈ کو دبانے سے اس کا ریٹ لاگو ہو جائے گا",
        contact: "رابطہ",
        whatsappGreeting: "السلام علیکم",
        whatsappBody: "آپ کے تخمینے کی تفصیل یہ ہے:",
        sendWhatsApp: "واٹس ایپ پر شیئر کریں",
        dateTimeLabel: "تاریخ اور وقت",
        whatsappDisclaimer: "نوٹ: قیمتیں مارکیٹ کے لائیو ریٹ کے مطابق تبدیل ہو سکتی ہیں۔",
        promoMessage: "جیولر ڈیسک کے ساتھ اپنے کاروبار کو ڈیجیٹل بنائیں! \nکسٹم سافٹ ویئر کے لیے رابطہ کریں:",
        iosInstallTitle: "آئی فون میں ایپ انسٹال کریں",
        iosInstallSteps: "نیچے دیئے گئے شیئر بٹن کو دبائیں اور پھر 'Add to Home Screen' منتخب کریں۔",

        landingTitle: "جیولر ڈیسک",
        landingDesc: "جیولرز کے لیے درست کیلکولیٹر۔ فوری تخمینہ لگائیں اور اپنے گاہکوں کے ساتھ خوبصورت پی ڈی ایف رسیدیں شیئر کریں۔",
        welcomeBack: "جیولر ڈیسک میں خوش آمدید",
        loginDesc: "سونے کا حساب کتاب اور فوری رسیدیں بنانے کے لیے سائن ان کریں۔",
        continueWithGoogle: "Google کے ساتھ جاری رکھیں",
        profileSetup: "آپ کی پروفائل",
        whatsappPrompt: "کیلکولیٹر کو ان لاک کرنے کے لیے اپنا فعال واٹس ایپ نمبر درج کریں۔",
        saveAndContinue: "محفوظ کریں اور جاری رکھیں",
        logout: "لاگ آؤٹ",
        businessNameLabel: "کاروبار / برانڈ کا نام (اختیاری)",
        supportMessage: "اگر اکاؤنٹ بنانے میں کوئی مسئلہ درپیش ہے تو براہ کرم سپورٹ سے رابطہ کریں، ہم آپ کا اکاؤنٹ سیٹ اپ کرنے میں خوشی محسوس کریں گے۔",
        contactSupport: "سپورٹ سے رابطہ کریں (تیار کردہ: سن فورج)",

        defaultPreferences: "بنیادی ترجیحات",
        defaultCurrency: "بنیادی کرنسی",
        defaultPhoneCode: "فون ڈائلنگ کوڈ",
        defaultUnit: "بنیادی وزن کی اکائی",
        defaultMetal: "بنیادی دھات",
        pdfDisclaimer: "نوٹ: متوقع قیمتیں مارکیٹ کے لائیو ریٹ کے مطابق تبدیل ہو سکتی ہیں۔",

        continueAsGuest: "مہمان کے طور پر استعمال کریں",
        signInToUnlock: "رسیدیں ان لاک کرنے کے لیے سائن ان کریں",
        guestUpsellText: "پی ڈی ایف رسیدیں ڈاؤن لوڈ کرنے اور واٹس ایپ پر بھیجنے کے لیے مفت اکاؤنٹ بنائیں۔",
        logoutConfirm: "کیا آپ واقعی لاگ آؤٹ کرنا چاہتے ہیں؟",
        yes: "جی ہاں، لاگ آؤٹ",
        no: "کینسل کریں",
        guestWatermark: "مہمان کا پیش نظارہ",
        sessionExpired: "آپ کا اکاؤنٹ کسی اور ڈیوائس پر لاگ ان ہوا ہے۔ آپ کو محفوظ طریقے سے لاگ آؤٹ کر دیا گیا ہے۔"
    }
};

const SUPPORTED_CURRENCIES = ['PKR', 'AED', 'USD', 'EUR', 'GBP', 'SAR', 'QAR', 'OMR'];
const COUNTRY_CODES = [
    {code: '+92', label: '🇵🇰 +92'},
    {code: '+971', label: '🇦🇪 +971'},
    {code: '+966', label: '🇸🇦 +966'},
    {code: '+44', label: '🇬🇧 +44'},
    {code: '+1', label: '🇺🇸 +1'},
    {code: '+91', label: '🇮🇳 +91'},
    {code: '+974', label: '🇶🇦 +974'},
    {code: '+968', label: '🇴🇲 +968'},
];

// Helper to generate and maintain a unique device identifier for tracking multi-device logouts
const getDeviceId = () => {
    if (typeof window === 'undefined') return 'unknown';
    let id = localStorage.getItem('jeweller_desk_device_id');
    if (!id) {
        id = 'dev_' + Math.random().toString(36).substring(2, 9) + Date.now().toString(36);
        localStorage.setItem('jeweller_desk_device_id', id);
    }
    return id;
};

export default function App() {
    // ==========================================
    // 5. APPLICATION STATE MANAGEMENT
    // ==========================================
    const [isAppLoading, setIsAppLoading] = useState<boolean>(true);
    const [isLoggingIn, setIsLoggingIn] = useState<boolean>(false);
    const [authError, setAuthError] = useState<string>('');
    const [currentDeviceId] = useState<string>(getDeviceId());

    // Auth States
    const [user, setUser] = useState<any>(null);
    const [userAccountId, setUserAccountId] = useState<string>('');
    const [hasPhoneProfile, setHasPhoneProfile] = useState<boolean>(false);
    const [profilePhoneInput, setProfilePhoneInput] = useState<string>('');
    const [profileCountryCode, setProfileCountryCode] = useState<string>('+92');
    const [profileBusinessName, setProfileBusinessName] = useState<string>('');

    // Default Preferences
    const [profileDefaultCurrency, setProfileDefaultCurrency] = useState<string>('PKR');
    const [profileDefaultPhoneCode, setProfileDefaultPhoneCode] = useState<string>('+92');
    const [profileDefaultUnit, setProfileDefaultUnit] = useState<'Tola' | 'Gram' | 'Ounce'>('Tola');
    const [profileDefaultMetal, setProfileDefaultMetal] = useState<'Gold' | 'Silver'>('Gold');

    const [isSavingProfile, setIsSavingProfile] = useState<boolean>(false);
    const [isGuest, setIsGuest] = useState<boolean>(false);

    // App UI States
    const [showLogoutConfirm, setShowLogoutConfirm] = useState<boolean>(false);
    const [lang, setLang] = useState<'en' | 'ur'>('en');
    const [currency, setCurrency] = useState<string>('PKR');
    const [countryCode, setCountryCode] = useState<string>('+92');

    // Calculator States
    const [tolaRate24k, setTolaRate24k] = useState<string>('');
    const [baseRateUnit, setBaseRateUnit] = useState<'Tola' | 'Gram' | 'Ounce'>('Tola');
    const [baseMetal, setBaseMetal] = useState<'Gold' | 'Silver'>('Gold');
    const [selectedCarat, setSelectedCarat] = useState<string>('22');

    const [customerName, setCustomerName] = useState<string>('');
    const [customerPhone, setCustomerPhone] = useState<string>('');

    const [weight, setWeight] = useState<string>('');
    // State Initialized with 'Gram' as default.
    const [weightUnit, setWeightUnit] = useState<'Gram' | 'Tola' | 'Ounce'>('Gram');
    const [wastage, setWastage] = useState<string>('');

    // Additional Costs Detailed Breakdown
    const [makingCost, setMakingCost] = useState<string>('');
    const [stonesCost, setStonesCost] = useState<string>('');
    const [otherCost, setOtherCost] = useState<string>('');
    const [discount, setDiscount] = useState<string>('');

    // System States
    const [isGenerating, setIsGenerating] = useState<boolean>(false);
    const [pdfLibraryLoaded, setPdfLibraryLoaded] = useState<boolean>(false);
    const [isOnline, setIsOnline] = useState<boolean>(typeof window !== 'undefined' ? navigator.onLine : true);
    const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
    const [showInstallBanner, setShowInstallBanner] = useState<boolean>(false);
    const [isIos, setIsIos] = useState<boolean>(false);
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

    // Security Blur State
    const [isAppBlurred, setIsAppBlurred] = useState<boolean>(false);

    // NEW: Ref points to the outer container for exact margin/background capturing
    const receiptRef = useRef<HTMLDivElement>(null);
    const t = translations[lang];
    const isRtl = lang === 'ur';

    const COLOR_CHARCOAL = '#1A1A1A';
    const COLOR_GOLD = '#D4AF37';

    // ==========================================
    // ANTI-SCREENSHOT & COPY BLOCKER (GUEST MODE)
    // ==========================================
    useEffect(() => {
        if (!isGuest) {
            setIsAppBlurred(false);
            return;
        }
        const handleCopy = (e: ClipboardEvent) => e.preventDefault();
        const handleKeyUp = (e: KeyboardEvent) => {
            if (e.key === 'PrintScreen') navigator.clipboard.writeText('');
        };
        const handleVisibilityChange = () => setIsAppBlurred(document.hidden);
        const handleBlur = () => setIsAppBlurred(true);
        const handleFocus = () => setTimeout(() => setIsAppBlurred(false), 200);

        document.addEventListener('copy', handleCopy as any);
        window.addEventListener('keyup', handleKeyUp);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('blur', handleBlur);
        window.addEventListener('focus', handleFocus);

        return () => {
            document.removeEventListener('copy', handleCopy as any);
            window.removeEventListener('keyup', handleKeyUp);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('blur', handleBlur);
            window.removeEventListener('focus', handleFocus);
        };
    }, [isGuest]);

    // ==========================================
    // 6. FIREBASE AUTHENTICATION LISTENER
    // ==========================================
    useEffect(() => {
        if (!auth) {
            setIsAppLoading(false);
            return;
        }

        const savedGuestStatus = localStorage.getItem('jeweller_desk_guest');
        if (savedGuestStatus === 'true') setIsGuest(true);

        let unsubscribeSnapshot: any = null;

        const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                // --- INSTANT OFFLINE LOCALSTORAGE RECOVERY LAYER ---
                const cachedHasProfile = localStorage.getItem(`jd_has_profile_${currentUser.uid}`) === 'true';
                const cachedAccountId = localStorage.getItem(`jd_account_id_${currentUser.uid}`) || '';
                const cachedCurrency = localStorage.getItem(`jd_currency_${currentUser.uid}`);
                const cachedPhoneCode = localStorage.getItem(`jd_phone_code_${currentUser.uid}`);
                const cachedUnit = localStorage.getItem(`jd_unit_${currentUser.uid}`);
                const cachedMetal = localStorage.getItem(`jd_metal_${currentUser.uid}`);

                if (cachedHasProfile) setHasPhoneProfile(true);
                if (cachedAccountId) setUserAccountId(cachedAccountId);
                if (cachedCurrency) setCurrency(cachedCurrency);
                if (cachedPhoneCode) setCountryCode(cachedPhoneCode);
                if (cachedUnit) setBaseRateUnit(cachedUnit as any);
                if (cachedMetal) setBaseMetal(cachedMetal as any);

                setUser(currentUser);
                setIsGuest(false);
                localStorage.removeItem('jeweller_desk_guest');

                try {
                    const docRef = doc(db, 'users', currentUser.uid);

                    // 1. Session Takeover Logic
                    if (isOnline) {
                        const snap = await getDoc(docRef);
                        // Claim this device session actively in the database if it doesn't match
                        if (snap.exists() && snap.data().currentDeviceId !== currentDeviceId) {
                            await setDoc(docRef, {currentDeviceId}, {merge: true});
                        }
                    }

                    // 2. Real-time Monitoring for Multi-Device Logins
                    unsubscribeSnapshot = onSnapshot(docRef, (docSnap) => {
                        if (docSnap.exists()) {
                            const data = docSnap.data();

                            // 3. SECURITY: Kickout Logic - If another device logs in, currentDeviceId will change
                            if (data.currentDeviceId && data.currentDeviceId !== currentDeviceId) {
                                console.warn("Session taken over by another device.");

                                // CLEAR LOCALSTORAGE LAYER ON AUTOMATIC KICKOUT
                                localStorage.removeItem(`jd_has_profile_${currentUser.uid}`);
                                localStorage.removeItem(`jd_account_id_${currentUser.uid}`);
                                localStorage.removeItem(`jd_currency_${currentUser.uid}`);
                                localStorage.removeItem(`jd_phone_code_${currentUser.uid}`);
                                localStorage.removeItem(`jd_unit_${currentUser.uid}`);
                                localStorage.removeItem(`jd_metal_${currentUser.uid}`);

                                signOut(auth);
                                setAuthError(translations[lang].sessionExpired);
                                return;
                            }

                            if (data.whatsapp) {
                                setHasPhoneProfile(true);
                                setUserAccountId(data.accountId || currentUser.uid);

                                // Keep local storage backup completely mirrored and fresh
                                localStorage.setItem(`jd_has_profile_${currentUser.uid}`, 'true');
                                localStorage.setItem(`jd_account_id_${currentUser.uid}`, data.accountId || currentUser.uid);

                                if (data.defaultCurrency) {
                                    setCurrency(data.defaultCurrency);
                                    localStorage.setItem(`jd_currency_${currentUser.uid}`, data.defaultCurrency);
                                }
                                if (data.defaultPhoneCode) {
                                    setCountryCode(data.defaultPhoneCode);
                                    localStorage.setItem(`jd_phone_code_${currentUser.uid}`, data.defaultPhoneCode);
                                }
                                if (data.defaultUnit) {
                                    setBaseRateUnit(data.defaultUnit);
                                    localStorage.setItem(`jd_unit_${currentUser.uid}`, data.defaultUnit);
                                }
                                if (data.defaultMetal) {
                                    setBaseMetal(data.defaultMetal);
                                    localStorage.setItem(`jd_metal_${currentUser.uid}`, data.defaultMetal);
                                }
                            } else {
                                setHasPhoneProfile(false);
                                localStorage.removeItem(`jd_has_profile_${currentUser.uid}`);
                            }
                        } else {
                            // If completely offline and we have a local cache flag, trust it over a blank database snapshot
                            if (!localStorage.getItem(`jd_has_profile_${currentUser.uid}`)) {
                            setHasPhoneProfile(false);
                        }
                        }

                        setIsAppLoading(false);
                        setIsLoggingIn(false);

                    }, (error) => {
                        console.error("Snapshot error:", error);
                        setIsAppLoading(false);
                        setIsLoggingIn(false);
                    });
                } catch (error: any) {
                    console.error("Error fetching user profile:", error);
                    if (!isOnline) {
                        setHasPhoneProfile(cachedHasProfile);
                    } else {
                        setAuthError("Database Error: " + error.message);
                    }
                    // Failsafe to dismiss loading on catch
                    setIsAppLoading(false);
                    setIsLoggingIn(false);
                }
            } else {
                setUser(null);
                setHasPhoneProfile(false);
                if (unsubscribeSnapshot) unsubscribeSnapshot();
                setIsAppLoading(false);
                setIsLoggingIn(false);
            }
        });

        return () => {
            unsubscribeAuth();
            if (unsubscribeSnapshot) unsubscribeSnapshot();
        };
    }, [isOnline, currentDeviceId]); // Re-run carefully observing offline state

    // ==========================================
    // 7. INITIALIZATION & PWA ENGINE
    // ==========================================
    useEffect(() => {
        const checkForUpdates = async () => {
            if (!navigator.onLine) return;
            // if (!('serviceWorker' in navigator)) return;

            if ('serviceWorker' in navigator) {
                window.addEventListener('load', () => {
                    navigator.serviceWorker.register('/sw.js')
                        .then(registration => {
                            console.log('ServiceWorker registration successful with scope: ', registration.scope);
                        })
                        .catch(err => {
                            console.log('ServiceWorker registration failed: ', err);
                        });
                });
            }
            try {
                const registration = await navigator.serviceWorker.ready;
                await registration.update();
                registration.addEventListener('updatefound', () => {
                    const newWorker = registration.installing;
                    if (newWorker) {
                        newWorker.addEventListener('statechange', () => {
                            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                                if ('caches' in window) {
                                    caches.keys().then(names => {
                                        for (let name of names) {
                                            caches.delete(name);
                                        }
                                    });
                                }
                                newWorker.postMessage({type: 'SKIP_WAITING'});
                                setTimeout(() => {
                                    window.location.reload();
                                }, 300);
                            }
                        });
                    }
                });
            } catch (err) {
                console.error("Auto-update failed:", err);
            }
        };

        checkForUpdates();

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') checkForUpdates();
        };
        window.addEventListener('visibilitychange', handleVisibilityChange);

        let refreshing = false;
        navigator.serviceWorker.addEventListener('controllerchange', () => {
            if (!refreshing) {
                refreshing = true;
                window.location.reload();
            }
        });

        if (typeof window !== 'undefined') {
            if ((window as any).html2pdf) {
                setPdfLibraryLoaded(true);
            } else {
                const script = document.createElement('script');
                script.src = "https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js";
                script.async = true;
                script.onload = () => setPdfLibraryLoaded(true);
                document.body.appendChild(script);
            }
        }

        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        const userAgent = window.navigator.userAgent.toLowerCase();
        const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
        const isStandalone = ('standalone' in window.navigator) && (window.navigator as any).standalone;

        if (isIOSDevice && !isStandalone) {
            const hasSeenPrompt = localStorage.getItem('ios_prompt_seen');
            if (!hasSeenPrompt) {
                setIsIos(true);
                setShowInstallBanner(true);
            }
        }

        const handleInstallPrompt = (e: Event) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setShowInstallBanner(true);
        };

        if (typeof window !== 'undefined' && (window as any).globalDeferredPrompt) {
            handleInstallPrompt((window as any).globalDeferredPrompt);
        }
        window.addEventListener('beforeinstallprompt', handleInstallPrompt);

        return () => {
            window.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener('beforeinstallprompt', handleInstallPrompt);
        };
    }, []);

    // ==========================================
    // 8. AUTHENTICATION HANDLERS
    // ==========================================
    const handleGoogleLogin = async () => {
        setAuthError('');
        setIsLoggingIn(true);
        try {
            if (auth && googleProvider) {
                await signInWithPopup(auth, googleProvider);
            } else {
                setAuthError("Firebase Auth not initialized correctly.");
                setIsLoggingIn(false);
            }
        } catch (error: any) {
            console.error("Login failed:", error);
            setAuthError("Login Failed: " + error.message);
            setIsLoggingIn(false);
        }
    };

    const handleGuestLogin = () => {
        setIsGuest(true);
        localStorage.setItem('jeweller_desk_guest', 'true');
    };

    const confirmLogout = () => setShowLogoutConfirm(true);

    const handleLogout = async () => {
        try {
            if (user) {
                localStorage.removeItem(`jd_has_profile_${user.uid}`);
                localStorage.removeItem(`jd_account_id_${user.uid}`);
                localStorage.removeItem(`jd_currency_${user.uid}`);
                localStorage.removeItem(`jd_phone_code_${user.uid}`);
                localStorage.removeItem(`jd_unit_${user.uid}`);
                localStorage.removeItem(`jd_metal_${user.uid}`);
            }

            if (auth) await signOut(auth);
            setIsGuest(false);
            localStorage.removeItem('jeweller_desk_guest');
            setShowLogoutConfirm(false);
        } catch (error) {
            console.error("Logout failed:", error);
        }
    };

    const handleSaveWhatsAppProfile = async () => {
        // Validation for Empty or Invalid WhatsApp length
        if (!profilePhoneInput || profilePhoneInput.length < 8) {
            setAuthError(t.whatsappRequired || "Please enter a valid WhatsApp number to continue.");
            return;
        }

        if (!db) return;
        setIsSavingProfile(true);
        setAuthError('');
        try {
            const newAccountId = `JD-${Math.floor(100000 + Math.random() * 900000)}`;
            const fullWhatsAppNumber = `${profileCountryCode} ${profilePhoneInput}`;

            const docRef = doc(db, 'users', user.uid);
            await setDoc(docRef, {
                accountId: newAccountId,
                whatsapp: fullWhatsAppNumber,
                businessName: profileBusinessName,
                email: user.email,
                name: user.displayName,
                defaultCurrency: profileDefaultCurrency,
                defaultPhoneCode: profileDefaultPhoneCode,
                defaultUnit: profileDefaultUnit,
                defaultMetal: profileDefaultMetal,
                currentDeviceId: currentDeviceId, // Ensure device ID is saved immediately to prevent loops
                createdAt: new Date().toISOString()
            }, {merge: true});

            // Apply defaults instantly
            setCurrency(profileDefaultCurrency);
            setCountryCode(profileDefaultPhoneCode);
            setBaseRateUnit(profileDefaultUnit);
            setBaseMetal(profileDefaultMetal);

            localStorage.setItem(`jd_has_profile_${user.uid}`, 'true');
            localStorage.setItem(`jd_account_id_${user.uid}`, newAccountId);
            localStorage.setItem(`jd_currency_${user.uid}`, profileDefaultCurrency);
            localStorage.setItem(`jd_phone_code_${user.uid}`, profileDefaultPhoneCode);
            localStorage.setItem(`jd_unit_${user.uid}`, profileDefaultUnit);
            localStorage.setItem(`jd_metal_${user.uid}`, profileDefaultMetal);

            setUserAccountId(newAccountId);
            setHasPhoneProfile(true);
        } catch (error: any) {
            console.error("Failed to save profile:", error);
            setAuthError("Save Failed: " + error.message);
        } finally {
            setIsSavingProfile(false);
        }
    };

    // ==========================================
    // 9. CALCULATION LOGIC
    // ==========================================
    const formatInputWithCommas = (val: string): string => {
        const clean = val.replace(/\D/g, '');
        if (!clean) return '';
        return Number(clean).toLocaleString('en-US');
    };

    const parseInputValue = (val: string): number => parseFloat(val.replace(/,/g, '')) || 0;

    const parsedTolaRate24k = parseInputValue(tolaRate24k);
    const parsedWeight = parseFloat(weight) || 0;
    const parsedWastage = parseFloat(wastage) || 0;

    // Detailed Breakdown Summation
    const parsedMakingCost = parseInputValue(makingCost);
    const parsedStonesCost = parseInputValue(stonesCost);
    const parsedOtherCost = parseInputValue(otherCost);
    const parsedDiscount = parseInputValue(discount);
    const totalAdditionalCost = parsedMakingCost + parsedStonesCost + parsedOtherCost;

    const TOLA_TO_GRAMS = 11.664;
    const OUNCE_TO_GRAMS = 31.1034768;

    // Safely calculate 1-Gram price
    let gramRate24k = 0;
    if (baseRateUnit === 'Tola') {
        gramRate24k = parsedTolaRate24k / TOLA_TO_GRAMS;
    } else if (baseRateUnit === 'Ounce') {
        gramRate24k = parsedTolaRate24k / OUNCE_TO_GRAMS;
    } else {
        gramRate24k = parsedTolaRate24k;
    }

    // Carat mapping including all international scales
    const caratRates: Record<string, { base: number; gram: number }> = {
        '24': {base: parsedTolaRate24k, gram: gramRate24k},
        '22': {base: parsedTolaRate24k * (22 / 24), gram: gramRate24k * (22 / 24)},
        '21': {base: parsedTolaRate24k * (21 / 24), gram: gramRate24k * (21 / 24)},
        '18': {base: parsedTolaRate24k * (18 / 24), gram: gramRate24k * (18 / 24)},
        '14': {base: parsedTolaRate24k * (14 / 24), gram: gramRate24k * (14 / 24)},
        '12': {base: parsedTolaRate24k * (12 / 24), gram: gramRate24k * (12 / 24)},
        '10': {base: parsedTolaRate24k * (10 / 24), gram: gramRate24k * (10 / 24)},
        '9': {base: parsedTolaRate24k * (9 / 24), gram: gramRate24k * (9 / 24)},
        '8': {base: parsedTolaRate24k * (8 / 24), gram: gramRate24k * (8 / 24)},
    };

    // If Silver is selected, lock internal math completely to pure 24K internally
    const activeRates = baseMetal === 'Silver' ? caratRates['24'] : (caratRates[selectedCarat] || caratRates['22']);

    let weightInGrams = parsedWeight;
    if (weightUnit === 'Tola') weightInGrams = parsedWeight * TOLA_TO_GRAMS;
    else if (weightUnit === 'Ounce') weightInGrams = parsedWeight * OUNCE_TO_GRAMS;

    const wastageInGrams = weightInGrams * (parsedWastage / 100);
    const netWeightInGrams = weightInGrams + wastageInGrams;

    const goldValue = netWeightInGrams * activeRates.gram;
    const subTotal = goldValue + totalAdditionalCost;
    const finalTotal = Math.max(0, subTotal - parsedDiscount);

    const formatCurrency = (val: number): string => `${currency} ` + val.toLocaleString('en-US', {maximumFractionDigits: 0});
    const formatGrams = (val: number): string => val.toFixed(3) + 'g';
    const currentDateTime = new Date().toLocaleString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true
    });

    // ==========================================
    // 10. LOG ACTIVITY TO DATABASE
    // ==========================================
    const logActivity = async (actionType: 'PDF_DOWNLOAD' | 'WHATSAPP_SENT') => {
        if (!user || !db || isGuest) return;
        try {
            await addDoc(collection(db, 'user_activities'), {
                accountId: userAccountId || user.uid,
                userId: user.uid,
                action: actionType,
                customerName: customerName || 'Walk-in Customer',
                customerPhone: customerPhone ? `${countryCode} ${customerPhone}` : 'No Number',
                baseMetal: baseMetal,
                goldCarat: baseMetal === 'Gold' ? selectedCarat : '24', // Track silver consistently
                netWeightGrams: netWeightInGrams,
                makingCost: parsedMakingCost,
                stonesCost: parsedStonesCost,
                otherCost: parsedOtherCost,
                totalValue: finalTotal,
                currency: currency,
                timestamp: new Date().toISOString()
            });
        } catch (error) {
            console.error("Failed to log activity:", error);
        }
    };

    // ==========================================
    // 11. GENERAL HANDLERS
    // ==========================================
    const trackEvent = (action: string, category: string, label: string, value?: number) => {
        const payload: any = {event_category: category, event_label: label};
        if (value) payload.value = value;
        if (navigator.onLine && typeof window !== 'undefined' && (window as any).gtag) {
            (window as any).gtag('event', action, payload);
        }
    };

    const toggleLanguage = () => {
        const newLang = lang === 'en' ? 'ur' : 'en';
        setLang(newLang);
        trackEvent('language_toggle', 'engagement', `Switched to ${newLang.toUpperCase()}`);
    };

    const clearFormFields = () => {
        setCustomerName('');
        setCustomerPhone('');
        setWeight('');
        setWastage('');
        setMakingCost('');
        setStonesCost('');
        setOtherCost('');
        setDiscount('');
        trackEvent('clear_fields', 'interaction', 'Cleared Inputs');
    };

    const handleWhatsAppClick = () => logActivity('WHATSAPP_SENT');

    const generateCustomerWhatsAppLink = () => {
        let rawPhone = customerPhone.replace(/\D/g, '');
        if (rawPhone.startsWith('0')) rawPhone = rawPhone.substring(1);
        const fullNumber = `${countryCode.replace('+', '')}${rawPhone}`;
        const namePart = customerName ? ` ${customerName}` : '';
        const greeting = `${t.whatsappGreeting}${namePart},`;

        let itemUnitText = t.grams;
        if (weightUnit === 'Tola') itemUnitText = t.tolas;
        if (weightUnit === 'Ounce') itemUnitText = t.ounces;

        // Localize the base rate unit for the WhatsApp message
        let baseRateUnitText = t.grams;
        if (baseRateUnit === 'Tola') baseRateUnitText = t.tolas;
        if (baseRateUnit === 'Ounce') baseRateUnitText = t.ounces;

        const metalString = baseMetal === 'Gold' ? `${selectedCarat}K ${t.gold}` : t.silver;

        // Safely parse literal newlines to prevent formatting breaks
        const formattedPromo = t.promoMessage.replace(/\\n/g, '\n');

        // Clean professional message with the applied rate and NO emojis
        const messageText = `${greeting}\n${t.whatsappBody}\n\n*${t.itemPurity}:* ${metalString}\n*${t.ratePerBaseUnit} (${baseRateUnitText}):* ${formatCurrency(activeRates.base)}\n*${t.totalWeight}:* ${parsedWeight || 0} ${itemUnitText}\n*${t.netWeight}:* ${formatGrams(netWeightInGrams)}\n*${t.totalPrice}:* ${formatCurrency(finalTotal)}\n\n*${t.dateTimeLabel}:* ${currentDateTime}\n\n_${t.whatsappDisclaimer}_\n\n--\n${formattedPromo}\n+92 340 0737675`;
        // const messageText = `${greeting}\n${t.whatsappBody}\n\n*${t.itemPurity}:* ${metalString}\n*${t.totalWeight}:* ${parsedWeight || 0} ${itemUnitText}\n*${t.netWeight}:* ${formatGrams(netWeightInGrams)}\n*${t.totalPrice}:* ${formatCurrency(finalTotal)}\n\n🕒 *${t.dateTimeLabel}:* ${currentDateTime}\n\n⚠️ _${t.whatsappDisclaimer}_\n\n--\n🚀 *${t.promoMessage}*\n📞 +92 340 0737675\n🌐 ${t.poweredBy}`;
        return `https://wa.me/${fullNumber}?text=${encodeURIComponent(messageText)}`;
    };

    const triggerAppInstall = async () => {
        if (isIos) {
            localStorage.setItem('ios_prompt_seen', 'true');
            setShowInstallBanner(false);
            return;
        }
        if (!deferredPrompt) return;
        trackEvent('pwa_install_click', 'engagement', 'User clicked Install App');
        deferredPrompt.prompt();
        await deferredPrompt.userChoice;
        setDeferredPrompt(null);
        setShowInstallBanner(false);
    };

    const dismissInstall = () => {
        if (isIos) localStorage.setItem('ios_prompt_seen', 'true');
        setShowInstallBanner(false);
    };

    const handleForceRefresh = async () => {
        setIsRefreshing(true);
        try {
            if ('caches' in window) {
                const cacheNames = await caches.keys();
                await Promise.all(cacheNames.map(name => caches.delete(name)));
            }
            if ('serviceWorker' in navigator) {
                const registrations = await navigator.serviceWorker.getRegistrations();
                await Promise.all(registrations.map(reg => reg.unregister()));
            }
            window.location.reload();
        } catch (error) {
            console.error("Failed to clear cache and refresh", error);
            setIsRefreshing(false);
        }
    };

// Advanced dynamic PDF generator fixing Mobile & Desktop borders natively
    const generatePDF = async () => {
        trackEvent('generate_receipt', 'conversion', `Receipt Generated`);
        setIsGenerating(true);

        const scrollContainer = document.getElementById('main-scroll-container');
        let originalOverflow = '';
        let originalHeight = '';

        try {
            if (typeof window !== 'undefined' && (window as any).html2pdf) {
                const element = receiptRef.current;
                if (!element) return;

                // THE ULTIMATE BLANK PAGE FIX:
                // Temporarily remove scroll restrictions in the REAL DOM.
                // This forces the browser to render the full exact coordinates of the receipt
                // without any virtual scroll clipping, guaranteeing a flawless capture!
                if (scrollContainer) {
                    originalOverflow = scrollContainer.style.overflow;
                    originalHeight = scrollContainer.style.height;
                    scrollContainer.style.overflow = 'visible';
                    scrollContainer.style.height = 'auto';
                }

                // Allow the DOM to settle and repaint before capturing
                await new Promise(resolve => setTimeout(resolve, 300));

                const widthPx = element.offsetWidth;
                const heightPx = element.offsetHeight;
                const widthInInches = 4.2;

                // Mathematically calculate the exact needed height + add a 0.05 buffer
                const heightInInches = (heightPx / widthPx) * widthInInches + 2;

                const opt = {
                    margin: 0.05,
                    filename: `JewellerDesk_${customerName ? customerName.replace(/\s+/g, '_') + '_' : ''}${selectedCarat}K.pdf`,
                    image: {type: 'jpeg', quality: 0.98},
                    pagebreak: { mode: ['avoid-all'] },
                    html2canvas: {
                        scale: 3, // Highly recommended for small text elements
                        useCORS: true,
                        scrollX: 0,
                        scrollY: 0
                    },
                    jsPDF: {unit: 'in', format: [4.3, heightInInches], orientation: 'portrait'}
                };

                await (window as any).html2pdf().set(opt).from(element).save();
                logActivity('PDF_DOWNLOAD');
            } else {
                window.print();
            }
        } catch (error) {
            console.error("PDF generation failed:", error);
            window.print();
        } finally {
            // Instantly restore original scroll behavior immediately after capture
            if (scrollContainer) {
                scrollContainer.style.overflow = originalOverflow;
                scrollContainer.style.height = originalHeight;
            }
            setIsGenerating(false);
        }
    };

    const triggerSystemPrint = () => {
        setTimeout(() => {
            window.print();
        }, 300);
    };

    // ==========================================
    // 12. UI RENDERING
    // ==========================================

    const renderInstallBanner = (extraClasses: string = "") => {
        if (!showInstallBanner) return null;
        return (
            <div className={`p-4 rounded-2xl shadow-lg border no-print flex flex-col gap-3 relative ${extraClasses}`}
                 style={{backgroundColor: '#2A2A2A', borderColor: '#444'}}>
                <div className="flex items-start gap-3 text-start">
                    <div className="p-3 rounded-xl bg-white flex items-center justify-center">
                        <JewellerDeskLogo className="w-6 h-6" letterColor={COLOR_CHARCOAL} diamondColor={COLOR_GOLD}
                                          width={24} height={24}/>
                    </div>
                    <div className="text-white text-left">
                        <h3 className="font-bold text-sm tracking-wide">{isIos ? t.iosInstallTitle : t.installPromptTitle}</h3>
                        <p className="text-xs text-gray-300 mt-1">{isIos ? t.iosInstallSteps : t.installPromptDesc}</p>
                    </div>
                </div>
                <div className="flex gap-2 justify-end mt-1">
                    <button onClick={dismissInstall}
                            className="px-4 py-2 text-xs font-semibold rounded-xl transition-all text-gray-300 hover:bg-[#444]">
                        {t.closeBtn}
                    </button>
                    {!isIos && (
                        <button onClick={triggerAppInstall}
                                className="px-4 py-2 text-xs font-bold rounded-xl transition-all shadow-md text-[#1A1A1A]"
                                style={{backgroundColor: COLOR_GOLD}}>
                            {t.installBtn}
                        </button>
                    )}
                </div>
            </div>
        );
    };

    const UpdateBanner = () => {
        const [showBanner, setShowBanner] = useState(false);
        const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);

        useEffect(() => {
            const handleUpdate = (event: any) => {
                setWaitingWorker(event.detail);
                setShowBanner(true);
            };

            window.addEventListener('pwa-update-available', handleUpdate);
            return () => window.removeEventListener('pwa-update-available', handleUpdate);
        }, []);

        const triggerUpdate = () => {
            if (waitingWorker) {
                // Tell the waiting Service Worker to force the swap
                waitingWorker.postMessage({ type: 'SKIP_WAITING' });

                // Reload the page to load the new cache
                waitingWorker.addEventListener('statechange', (e: any) => {
                    if (e.target.state === 'activated') {
                        window.location.reload();
                    }
                });
            }
        };

        if (!showBanner) return null;

        return (
            <div className="fixed bottom-4 left-4 right-4 bg-yellow-500 text-black p-4 rounded-lg shadow-lg flex justify-between items-center z-50">
                <span>A new version of the app is available!</span>
                <button
                    onClick={triggerUpdate}
                    className="bg-black text-white px-4 py-2 rounded font-bold"
                >
                    Update Now
                </button>
            </div>
        );
    };
    // --- 1. SPLASH SCREEN ---
    if (isAppLoading || isLoggingIn) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-[#1A1A1A] no-print">
                <div className="p-5 rounded-2xl mb-4 animate-pulse flex items-center justify-center bg-white">
                    <JewellerDeskLogo className="w-16 h-16" letterColor={COLOR_CHARCOAL} diamondColor={COLOR_GOLD}
                                      width={64} height={64}/>
                </div>
                <h1 className="text-3xl tracking-tight mb-2" style={{color: COLOR_GOLD}}>
                    <span className="font-extrabold">Jeweller</span> <span className="font-normal">Desk</span>
                </h1>
                <div className="flex gap-1.5 mt-8">
                    <div className="w-2.5 h-2.5 rounded-full animate-bounce"
                         style={{backgroundColor: COLOR_GOLD, animationDelay: '0ms'}}></div>
                    <div className="w-2.5 h-2.5 rounded-full animate-bounce"
                         style={{backgroundColor: COLOR_GOLD, animationDelay: '150ms'}}></div>
                    <div className="w-2.5 h-2.5 rounded-full animate-bounce"
                         style={{backgroundColor: COLOR_GOLD, animationDelay: '300ms'}}></div>
                </div>
            </div>
        );
    }

    // --- 2. LOGIN SCREEN ---
    if (!user && !isGuest) {
        return (
            <div dir={isRtl ? 'rtl' : 'ltr'}
                 className={`min-h-screen flex flex-col items-center justify-center p-6 ${isRtl ? 'font-urdu' : 'font-sans'}`}
                 style={{backgroundColor: COLOR_CHARCOAL}}>

                {renderInstallBanner("w-full max-w-sm mb-6")}

                <div className="max-w-xl text-center mb-8 mt-4">
                    <div className="inline-block p-4 rounded-3xl mb-6 bg-white">
                        <JewellerDeskLogo className="w-16 h-16" letterColor={COLOR_CHARCOAL} diamondColor={COLOR_GOLD}
                                          width={64} height={64}/>
                    </div>
                    <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-4 leading-tight">
                        {t.landingTitle}
                    </h1>
                    <p className="text-gray-400 text-sm md:text-base leading-relaxed px-4">
                        {t.landingDesc}
                    </p>
                </div>

                <div
                    className="w-full max-w-sm bg-[#2A2A2A] p-8 rounded-3xl shadow-2xl border border-gray-700 flex flex-col items-center text-center">
                    <h2 className="text-xl font-bold text-white mb-2">{t.welcomeBack}</h2>
                    <p className="text-sm text-gray-400 mb-6">{t.loginDesc}</p>

                    {authError && (
                        <div
                            className="w-full p-4 mb-6 bg-red-500/10 border border-red-500 text-red-500 text-sm font-bold rounded-xl text-left">
                            ⚠️ {authError}
                        </div>
                    )}

                    <button
                        onClick={handleGoogleLogin}
                        className="w-full py-3.5 px-4 bg-white hover:bg-gray-100 text-gray-900 font-bold rounded-xl shadow-lg transition-all flex items-center justify-center gap-3"
                    >
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                            <path
                                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                fill="#4285F4"/>
                            <path
                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                fill="#34A853"/>
                            <path
                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                fill="#FBBC05"/>
                            <path
                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                fill="#EA4335"/>
                        </svg>
                        {t.continueWithGoogle}
                    </button>

                    <div className="flex items-center gap-3 my-4 w-full">
                        <div className="h-px bg-gray-700 flex-1"></div>
                        <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">OR</span>
                        <div className="h-px bg-gray-700 flex-1"></div>
                    </div>

                    <button
                        onClick={handleGuestLogin}
                        className="w-full py-3.5 px-4 bg-[#1A1A1A] hover:bg-black text-gray-300 font-bold rounded-xl border border-gray-600 transition-all flex items-center justify-center gap-2"
                    >
                        <Calculator className="w-4 h-4"/>
                        {t.continueAsGuest}
                    </button>

                    <button onClick={toggleLanguage}
                            className="mt-6 flex items-center justify-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-300 transition-colors">
                        <Globe className="w-3.5 h-3.5"/> {lang === 'en' ? 'اردو میں استعمال کریں' : 'Switch to English'}
                    </button>

                    <div className="mt-6 pt-5 border-t border-gray-700 w-full text-center">
                        <p className="text-xs text-gray-400 mb-3 leading-relaxed">{t.supportMessage}</p>
                        <a href="https://wa.me/+923400737675" target="_blank" rel="noopener noreferrer"
                           className="inline-flex items-center justify-center gap-1.5 text-xs font-bold transition-colors"
                           style={{color: COLOR_GOLD}}>
                            <MessageCircle className="w-3.5 h-3.5"/> {t.contactSupport}
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    // --- 3. WHATSAPP COLLECTION SCREEN & PREFERENCES ---
    if (user && !hasPhoneProfile && !isGuest) {
        return (
            <div dir={isRtl ? 'rtl' : 'ltr'}
                 className={`min-h-screen flex flex-col items-center justify-center p-4 py-8 ${isRtl ? 'font-urdu' : 'font-sans'}`}
                 style={{backgroundColor: COLOR_CHARCOAL}}>
                {renderInstallBanner("w-full max-w-sm mb-4")}

                <div
                    className="w-full max-w-sm bg-[#2A2A2A] p-6 rounded-3xl shadow-2xl border border-gray-700 flex flex-col items-center text-center">
                    <div className="w-16 h-16 rounded-full bg-gray-700 mb-4 overflow-hidden border-2"
                         style={{borderColor: COLOR_GOLD}}>
                        {user.photoURL ?
                            <img src={user.photoURL} alt="Profile" className="w-full h-full object-cover"/> :
                            <User className="w-8 h-8 m-4 text-gray-400"/>}
                    </div>
                    <h2 className="text-xl font-bold text-white mb-2">{t.profileSetup}</h2>
                    <p className="text-sm text-gray-400 mb-6">{t.whatsappPrompt}</p>

                    {authError && (
                        <div
                            className="w-full p-4 mb-6 bg-red-500/10 border border-red-500 text-red-500 text-sm font-bold rounded-xl text-left">
                            ⚠️ {authError}
                        </div>
                    )}

                    <div
                        className="flex w-full bg-[#1A1A1A] border border-gray-600 rounded-xl shadow-inner focus-within:ring-2 focus-within:ring-[#D4AF37] focus-within:border-[#D4AF37] transition-all overflow-hidden mb-4">
                        <select
                            className="w-[105px] shrink-0 border-none border-r border-gray-700 bg-transparent p-3 text-white outline-none text-sm font-medium cursor-pointer focus:ring-0 [&>option]:bg-[#1A1A1A] [&>option]:text-white"
                            value={profileCountryCode}
                            style={{background: '#1A1A1A', color: '#fff'}}
                            onChange={(e) => setProfileCountryCode(e.target.value)}
                        >
                            {COUNTRY_CODES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                        </select>
                        <input
                            type="tel"
                            className="flex-1 w-full bg-transparent p-3 text-white outline-none placeholder-gray-500 font-medium"
                            placeholder="300 1234567"
                            value={profilePhoneInput}
                            onChange={(e) => setProfilePhoneInput(e.target.value)}
                        />
                    </div>

                    <div
                        className="flex w-full bg-[#1A1A1A] border border-gray-600 rounded-xl shadow-inner focus-within:ring-2 focus-within:ring-[#D4AF37] focus-within:border-[#D4AF37] transition-all overflow-hidden mb-6">
                        <div className="flex items-center justify-center px-4 border-r border-gray-700 bg-[#222]">
                            <Briefcase className="w-4 h-4 text-gray-400"/>
                        </div>
                        <input
                            type="text"
                            className="flex-1 w-full bg-transparent p-3 text-white outline-none placeholder-gray-500 font-medium text-sm"
                            placeholder={t.businessNameLabel}
                            value={profileBusinessName}
                            onChange={(e) => setProfileBusinessName(e.target.value)}
                        />
                    </div>

                    <div className="w-full mb-6 border-t border-gray-700 pt-5 text-left">
                        <h3 className="text-sm font-bold text-gray-300 mb-4 flex items-center gap-2">
                            <Settings className="w-4 h-4" style={{color: COLOR_GOLD}}/> {t.defaultPreferences}
                        </h3>

                        <div className="space-y-3">
                            <div className="flex w-full bg-[#1A1A1A] border border-gray-600 rounded-xl overflow-hidden">
                                <div
                                    className="flex items-center justify-center px-4 border-r border-gray-700 bg-[#222] text-xs font-bold text-gray-400 w-1/3">
                                    {t.defaultMetal}
                                </div>
                                <select
                                    className="flex-1 border-none bg-transparent p-3 text-white outline-none text-sm font-medium cursor-pointer [&>option]:bg-[#1A1A1A] [&>option]:text-white"
                                    value={profileDefaultMetal}
                                    style={{background: '#1A1A1A', color: '#fff'}}
                                    onChange={(e) => setProfileDefaultMetal(e.target.value as 'Gold' | 'Silver')}
                                >
                                    <option value="Gold">{t.gold}</option>
                                    <option value="Silver">{t.silver}</option>
                                </select>
                            </div>

                            <div className="flex w-full bg-[#1A1A1A] border border-gray-600 rounded-xl overflow-hidden">
                                <div
                                    className="flex items-center justify-center px-4 border-r border-gray-700 bg-[#222] text-xs font-bold text-gray-400 w-1/3">
                                    {t.defaultCurrency}
                                </div>
                                <select
                                    className="flex-1 border-none bg-transparent p-3 text-white outline-none text-sm font-medium cursor-pointer [&>option]:bg-[#1A1A1A] [&>option]:text-white"
                                    value={profileDefaultCurrency}
                                    style={{background: '#1A1A1A', color: '#fff'}}
                                    onChange={(e) => setProfileDefaultCurrency(e.target.value)}
                                >
                                    {SUPPORTED_CURRENCIES.map(curr => <option key={curr} value={curr}>{curr}</option>)}
                                </select>
                            </div>

                            <div className="flex w-full bg-[#1A1A1A] border border-gray-600 rounded-xl overflow-hidden">
                                <div
                                    className="flex items-center justify-center px-4 border-r border-gray-700 bg-[#222] text-xs font-bold text-gray-400 w-1/3">
                                    {t.defaultUnit}
                                </div>
                                <select
                                    className="flex-1 border-none bg-transparent p-3 text-white outline-none text-sm font-medium cursor-pointer [&>option]:bg-[#1A1A1A] [&>option]:text-white"
                                    value={profileDefaultUnit}
                                    onChange={(e) => setProfileDefaultUnit(e.target.value as 'Gram' | 'Tola' | 'Ounce')}
                                    style={{background: '#1A1A1A', color: '#fff'}}
                                >
                                    <option value="Tola">{t.tolas}</option>
                                    <option value="Gram">{t.grams}</option>
                                    <option value="Ounce">{t.ounces}</option>
                                </select>
                            </div>

                            <div className="flex w-full bg-[#1A1A1A] border border-gray-600 rounded-xl overflow-hidden">
                                <div
                                    className="flex items-center justify-center px-4 border-r border-gray-700 bg-[#222] text-xs font-bold text-gray-400 w-1/3">
                                    {t.defaultPhoneCode}
                                </div>
                                <select
                                    className="flex-1 border-none bg-transparent p-3 text-white outline-none text-sm font-medium cursor-pointer [&>option]:bg-[#1A1A1A] [&>option]:text-white"
                                    value={profileDefaultPhoneCode}
                                    style={{background: '#1A1A1A', color: '#fff'}}
                                    onChange={(e) => setProfileDefaultPhoneCode(e.target.value)}
                                >
                                    {COUNTRY_CODES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                                </select>
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={handleSaveWhatsAppProfile}
                        disabled={isSavingProfile}
                        className="w-full text-white font-bold py-3.5 px-4 rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 mt-2"
                        style={{backgroundColor: COLOR_GOLD, color: COLOR_CHARCOAL}}
                    >
                        {isSavingProfile ? t.processing : t.saveAndContinue}
                    </button>

                    <button onClick={confirmLogout}
                            className="mt-6 text-xs font-semibold text-gray-500 hover:text-gray-300 flex items-center gap-1">
                        <LogOut className="w-3 h-3"/> Cancel & {t.logout}
                    </button>
                </div>
            </div>
        );
    }

    // --- 4. MAIN APP INTERFACE ---
    return (
        <>
            <style>
                {`
          @media print {
            @page { size: portrait; margin: 0in; }
            body { background-color: white !important; color: black !important; }
            body * { visibility: hidden; }
            #pdf-capture-wrapper, #pdf-capture-wrapper * { visibility: visible !important; }
            #pdf-capture-wrapper .no-print, #pdf-capture-wrapper .no-print * {
              display: none !important; visibility: hidden !important;
            }
            #pdf-capture-wrapper { 
              position: relative !important; left: 0 !important; top: 0 !important; transform: none !important;
              margin: 0.2in auto !important; width: 380px !important; max-width: 380px !important;
            }
            .no-print { display: none !important; }
          }
          .focus-gold-ring:focus-within {
            outline: none !important;
            box-shadow: 0 0 0 2px ${COLOR_GOLD} !important;
            border-color: ${COLOR_GOLD} !important;
          }
          input:focus, select:focus {
            outline: none !important;
            box-shadow: none !important;
          }
        `}
            </style>

            <div dir={isRtl ? 'rtl' : 'ltr'}
                 className={`max-w-md mx-auto min-h-screen shadow-2xl relative flex flex-col bg-[#F9FAFB] ${isRtl ? 'font-urdu' : 'font-sans'}`}>

                <header
                    className="text-white p-4 shadow-md sticky top-0 z-50 no-print flex justify-between items-center"
                    style={{backgroundColor: COLOR_CHARCOAL}}>
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg flex items-center justify-center bg-white">
                            <JewellerDeskLogo className="w-6 h-6" letterColor={COLOR_CHARCOAL} diamondColor={COLOR_GOLD}
                                              width={24} height={24}/>
                        </div>
                        <h1 className="text-2xl tracking-tight" style={{color: COLOR_GOLD}}>
                            <span className="font-extrabold">Jeweller</span> <span className="font-normal">Desk</span>
                        </h1>
                    </div>
                    <div className="flex items-center gap-2">
                        {!isGuest && (
                            <button onClick={confirmLogout}
                                    className="p-2 hover:bg-gray-800 rounded-lg border border-gray-700 transition-colors bg-[#2A2A2A] text-gray-400 hover:text-red-400"
                                    title={t.logout}>
                                <LogOut className="w-4 h-4"/>
                            </button>
                        )}
                        <button onClick={handleForceRefresh} disabled={isRefreshing}
                                className="p-2 hover:bg-gray-800 rounded-lg border border-gray-700 transition-colors bg-[#2A2A2A] flex items-center justify-center"
                                title="Sync App">
                            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`}
                                       style={{color: COLOR_GOLD}}/>
                        </button>
                        <button onClick={toggleLanguage}
                                className="flex items-center gap-1 hover:bg-gray-800 px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-700 transition-colors bg-[#2A2A2A]">
                            <Globe className="w-4 h-4" style={{color: COLOR_GOLD}}/>
                            {lang === 'en' ? 'اردو' : 'EN'}
                        </button>
                    </div>
                </header>


                <main id="main-scroll-container" className="flex-1 overflow-y-auto p-4 space-y-6 pb-36">
                    {renderInstallBanner()}
                    {/* NEW: Global Clear Fields Button - Fixed Chat Bubble Overlay */}
                    {(customerName || customerPhone || weight || wastage || makingCost || stonesCost || otherCost || discount) ? (
                        <div className="fixed top-[85px] left-0 right-0 max-w-md mx-auto w-full z-40 flex justify-end px-4 no-print pointer-events-none">
                            <button onClick={clearFormFields}
                                    className="pointer-events-auto flex items-center gap-1.5 px-4 py-2 bg-red-50 backdrop-blur-md hover:bg-red-100 text-red-600 border border-red-200 rounded-full text-sm font-bold transition-all shadow-[0_8px_20px_rgb(0,0,0,0.12)] active:scale-95">
                                <RotateCcw className="w-4 h-4"/> {t.clearFields}
                            </button>
                        </div>
                    ) : null}
                    {!isGuest && (
                        <section
                            className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 space-y-4 no-print">
                            <div className="flex justify-between items-center mb-1">
                                <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wider">{t.customerDetails}</h2>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                                    <User className="w-4 h-4 text-gray-400"/> {t.customerName}
                                </label>
                                <div
                                    className="flex w-full bg-gray-50 border border-gray-200 rounded-xl shadow-sm focus-gold-ring overflow-hidden">
                                    <input
                                        type="text"
                                        className="flex-1 min-w-0 bg-transparent p-3 outline-none border-none transition-all"
                                        placeholder={isRtl ? "نام درج کریں" : "Enter name"}
                                        value={customerName}
                                        onChange={e => setCustomerName(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                                    <Phone className="w-4 h-4 text-gray-400"/> {t.customerPhone}
                                </label>
                                <div
                                    className="flex w-full bg-gray-50 border border-gray-200 rounded-xl shadow-sm focus-gold-ring overflow-hidden">
                                    <select
                                        className="w-[105px] shrink-0 border-none border-r border-gray-200 bg-gray-100 p-3 outline-none text-sm font-medium cursor-pointer hover:bg-gray-200 transition-colors"
                                        value={countryCode}
                                        onChange={(e) => setCountryCode(e.target.value)}
                                    >
                                        {COUNTRY_CODES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                                    </select>
                                    <input
                                        type="tel"
                                        className="flex-1 min-w-0 bg-transparent p-3 outline-none border-none"
                                        placeholder="300xxxxxxx"
                                        value={customerPhone}
                                        onChange={e => setCustomerPhone(e.target.value)}
                                    />
                                </div>
                            </div>
                        </section>
                    )}

                    <section className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 space-y-4 no-print">
                        <div className="flex justify-between items-center mb-1">
                            <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wider">{t.marketRateBase}</h2>
                        </div>
                        <div className="space-y-3">
                            {/* Row 1: Metal & Unit */}
                            <div className="flex w-full gap-3">
                                <div className="flex-1 bg-gray-50 border border-gray-200 rounded-xl shadow-sm focus-gold-ring overflow-hidden">
                                    <select
                                        className="w-full px-3 py-3.5 bg-transparent border-none text-gray-800 font-bold text-sm outline-none cursor-pointer hover:bg-gray-100 transition-colors text-center"
                                        value={baseMetal}
                                        onChange={(e) => setBaseMetal(e.target.value as 'Gold' | 'Silver')}
                                    >
                                        <option value="Gold">{t.gold}</option>
                                        <option value="Silver">{t.silver}</option>
                                    </select>
                                </div>
                                <div className="flex-1 bg-gray-50 border border-gray-200 rounded-xl shadow-sm focus-gold-ring overflow-hidden">
                                    <select
                                        className="w-full px-3 py-3.5 bg-transparent border-none text-gray-800 font-bold text-sm outline-none cursor-pointer hover:bg-gray-100 transition-colors text-center"
                                        value={baseRateUnit}
                                        onChange={(e) => setBaseRateUnit(e.target.value as any)}
                                    >
                                        <option value="Tola">{t.tolas}</option>
                                        <option value="Gram">{t.grams}</option>
                                        <option value="Ounce">{t.ounces}</option>
                                    </select>
                                </div>
                            </div>

                            {/* Row 2: Currency & Rate */}
                            <div className="flex bg-white border border-gray-200 rounded-xl shadow-sm focus-gold-ring overflow-hidden">
                                <select
                                    className="w-[90px] shrink-0 px-2 py-4 bg-gray-50 border-none border-r border-gray-200 text-gray-700 font-bold text-sm outline-none cursor-pointer hover:bg-gray-100 transition-colors text-center"
                                    value={currency}
                                    onChange={(e) => setCurrency(e.target.value)}
                                >
                                    {SUPPORTED_CURRENCIES.map(curr => <option key={curr} value={curr}>{curr}</option>)}
                                </select>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    className="flex-1 min-w-0 px-4 py-4 bg-transparent outline-none w-full border-none font-black text-xl text-gray-900 tracking-wider text-center"
                                    placeholder="450,000"
                                    value={tolaRate24k}
                                    onChange={e => setTolaRate24k(formatInputWithCommas(e.target.value))}
                                />
                            </div>
                        </div>
                    </section>

                    {parsedTolaRate24k > 0 && baseMetal === 'Gold' && (
                        <section className="p-5 rounded-2xl shadow-sm border no-print space-y-3" style={{
                            backgroundColor: 'rgba(212, 175, 55, 0.05)',
                            borderColor: 'rgba(212, 175, 55, 0.2)'
                        }}>
                            <div className="flex justify-between items-center">
                                <h2 className="text-sm font-bold uppercase tracking-wider flex items-center gap-2"
                                    style={{color: COLOR_CHARCOAL}}>
                                    <TrendingUp className="w-4 h-4" style={{color: COLOR_GOLD}}/> {t.perGramRates}
                                </h2>
                            </div>
                            <div className="grid grid-cols-3 gap-3 mt-3">
                                {(['24', '22', '21', '18', '14', '12', '10', '9', '8'] as const).map((carat) => {
                                    const isSelected = selectedCarat === carat;
                                    return (
                                        <button
                                            key={carat}
                                            onClick={() => setSelectedCarat(carat)}
                                            className={`p-2 rounded-xl border text-center flex flex-col items-center justify-center transition-all cursor-pointer ${
                                                isSelected ? 'shadow-md scale-102 ring-2' : 'bg-white hover:bg-gray-50'
                                            }`}
                                            style={isSelected ? {
                                                backgroundColor: COLOR_CHARCOAL,
                                                borderColor: COLOR_CHARCOAL,
                                                outlineColor: COLOR_GOLD
                                            } : {borderColor: '#E5E7EB'}}
                                        >
                                            <span className={`text-xs font-black mb-1`}
                                                  style={{color: isSelected ? COLOR_GOLD : '#6B7280'}}>
                                                {carat}K {isSelected && `✓`}
                                            </span>
                                            <span className={`text-xs font-bold`}
                                                  style={{color: isSelected ? 'white' : '#1F2937'}}>
                                                {formatCurrency(caratRates[carat].gram)}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    <section className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 space-y-5 no-print">
                        <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wider">{t.itemDetails}</h2>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                                <Scale className="w-4 h-4 text-gray-400"/> {t.totalWeight}
                            </label>
                            <div
                                className="flex w-full bg-gray-50 border border-gray-200 rounded-xl shadow-sm focus-gold-ring overflow-hidden">
                                <input
                                    type="number"
                                    inputMode="decimal"
                                    className="flex-1 min-w-0 bg-transparent p-3 outline-none border-none"
                                    placeholder="0.00"
                                    value={weight}
                                    onChange={e => setWeight(e.target.value)}
                                />
                                <select
                                    className="w-[100px] shrink-0 border-none border-l border-gray-200 bg-gray-100 p-3 outline-none cursor-pointer hover:bg-gray-200 transition-colors"
                                    value={weightUnit}
                                    onChange={e => setWeightUnit(e.target.value as 'Gram' | 'Tola' | 'Ounce')}
                                >
                                    <option value="Gram">{t.grams}</option>
                                    <option value="Tola">{t.tolas}</option>
                                    <option value="Ounce">{t.ounces}</option>
                                </select>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                                <Percent className="w-4 h-4 text-gray-400"/> {t.wastagePercent}
                            </label>
                            <div
                                className="flex w-full bg-gray-50 border border-gray-200 rounded-xl shadow-sm focus-gold-ring overflow-hidden">
                                <input
                                    type="number"
                                    inputMode="decimal"
                                    className="flex-1 min-w-0 p-3 bg-transparent outline-none w-full border-none"
                                    placeholder="10"
                                    value={wastage}
                                    onChange={e => setWastage(e.target.value)}
                                />
                                <div
                                    className="flex items-center justify-center px-4 shrink-0 bg-gray-100 text-gray-500 font-bold text-sm border-l border-gray-200">%
                                </div>
                            </div>
                        </div>

                        {!isGuest ? (
                            <div className="pt-2 border-t border-gray-100">
                                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">{t.additionalCosts}</h3>

                                <div className="space-y-3">
                                    <div>
                                        <label
                                            className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                                            <PlusCircle className="w-4 h-4 text-gray-400"/> {t.makingCharges}
                                        </label>
                                        <div
                                            className="flex w-full bg-gray-50 border border-gray-200 rounded-xl shadow-sm focus-gold-ring overflow-hidden">
                                            <div
                                                className="flex items-center justify-center px-4 shrink-0 bg-gray-100 text-gray-600 font-bold text-sm border-r border-gray-200">{currency}</div>
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                className="flex-1 min-w-0 p-3 bg-transparent outline-none w-full border-none"
                                                placeholder="0"
                                                value={makingCost}
                                                onChange={e => setMakingCost(formatInputWithCommas(e.target.value))}
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label
                                            className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                                            <PlusCircle className="w-4 h-4 text-gray-400"/> {t.stoneCharges}
                                        </label>
                                        <div
                                            className="flex w-full bg-gray-50 border border-gray-200 rounded-xl shadow-sm focus-gold-ring overflow-hidden">
                                            <div
                                                className="flex items-center justify-center px-4 shrink-0 bg-gray-100 text-gray-600 font-bold text-sm border-r border-gray-200">{currency}</div>
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                className="flex-1 min-w-0 p-3 bg-transparent outline-none w-full border-none"
                                                placeholder="0"
                                                value={stonesCost}
                                                onChange={e => setStonesCost(formatInputWithCommas(e.target.value))}
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label
                                            className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                                            <PlusCircle className="w-4 h-4 text-gray-400"/> {t.otherCharges}
                                        </label>
                                        <div
                                            className="flex w-full bg-gray-50 border border-gray-200 rounded-xl shadow-sm focus-gold-ring overflow-hidden">
                                            <div
                                                className="flex items-center justify-center px-4 shrink-0 bg-gray-100 text-gray-600 font-bold text-sm border-r border-gray-200">{currency}</div>
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                className="flex-1 min-w-0 p-3 bg-transparent outline-none w-full border-none"
                                                placeholder="0"
                                                value={otherCost}
                                                onChange={e => setOtherCost(formatInputWithCommas(e.target.value))}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="pt-2 border-t border-gray-100">
                                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                                    <PlusCircle className="w-4 h-4 text-gray-400"/> {t.additionalCosts}
                                </label>
                                <div
                                    className="flex w-full bg-gray-50 border border-gray-200 rounded-xl shadow-sm focus-gold-ring overflow-hidden">
                                    <div
                                        className="flex items-center justify-center px-4 shrink-0 bg-gray-100 text-gray-600 font-bold text-sm border-r border-gray-200">{currency}</div>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        className="flex-1 min-w-0 p-3 bg-transparent outline-none w-full border-none"
                                        placeholder="0"
                                        value={otherCost}
                                        onChange={e => setOtherCost(formatInputWithCommas(e.target.value))}
                                    />
                                </div>
                            </div>
                        )}

                        <div className="pt-2 border-t border-gray-100">
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                                <Tag className="w-4 h-4 text-gray-400"/> {t.discount}
                            </label>
                            <div
                                className="flex w-full bg-gray-50 border border-gray-200 rounded-xl shadow-sm focus-gold-ring overflow-hidden">
                                <div
                                    className="flex items-center justify-center px-4 shrink-0 bg-gray-100 text-gray-600 font-bold text-sm border-r border-gray-200">{currency}</div>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    className="flex-1 min-w-0 p-3 bg-transparent outline-none w-full text-red-600 font-medium border-none"
                                    placeholder="0"
                                    value={discount}
                                    onChange={e => setDiscount(formatInputWithCommas(e.target.value))}
                                />
                            </div>
                        </div>
                    </section>

                    {/* WRAPPER FIX: Encapsulating the receipt in a clean grey background guarantees it renders identically to the HTML view in html2canvas */}
                    <div className="flex justify-center w-full no-print">
                        <div
                            ref={receiptRef}
                            id="pdf-capture-wrapper"
                            className="w-full flex justify-center bg-[#F9FAFB]"
                            style={{padding: '20px', maxWidth: '440px', boxSizing: 'border-box'}}
                        >
                            <section
                                id="receipt-section"
                                className={`bg-white p-6 text-sm relative overflow-hidden w-full max-w-[380px] select-none rounded-2xl shadow-sm border-none  ${isGuest && isAppBlurred ? 'blur-md' : 'blur-none'} transition-all duration-200`}
                                style={{WebkitUserSelect: 'none', WebkitTouchCallout: 'none', margin: '0 auto'}}
                            >
                                {/* Added rounded-t-2xl to prevent the gold bar from overflowing the card's rounded corners */}
                                <div className="absolute top-0 left-0 right-0 h-1.5 rounded-t-2xl"
                                     style={{backgroundColor: COLOR_GOLD}}></div>
                                <div className="absolute bottom-0 left-0 right-0 h-1.5 rounded-t-2xl" data-html2canvas-ignore="true"
                                     style={{backgroundColor: COLOR_GOLD}}></div>

                                {isGuest && (
                                    <div
                                        className="absolute inset-0 z-20 pointer-events-none"
                                        style={{
                                            backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(`<svg width='220' height='220' xmlns='http://www.w3.org/2000/svg'><text x='50%' y='50%' font-size='22' font-weight='900' fill='rgba(0,0,0,0.06)' font-family='sans-serif' text-anchor='middle' dominant-baseline='middle' transform='rotate(-45 110 110)'>${t.guestWatermark}</text></svg>`)}")`,
                                            backgroundRepeat: 'repeat'
                                        }}
                                    />
                                )}

                                <div className="flex flex-col items-center mb-5 mt-4 relative z-10">
                                    <div className="mb-2 flex items-center justify-center bg-white p-2 rounded-xl">
                                        <JewellerDeskLogo className="w-12 h-12" letterColor={COLOR_CHARCOAL}
                                                          diamondColor={COLOR_GOLD} width={48} height={48}/>
                                    </div>
                                    <h2 className="text-xl tracking-tight" style={{color: COLOR_CHARCOAL}}>
                                        <span className="font-extrabold uppercase">Jeweller</span> <span
                                        className="font-normal uppercase">Desk</span>
                                    </h2>
                                    <h3 className="text-sm font-bold uppercase tracking-widest mt-1 text-gray-400">{t.estimate}</h3>
                                    <p className="text-gray-400 text-[10px] mt-1 font-medium">{currentDateTime}</p>
                                </div>

                                {!isGuest && (customerName || customerPhone) && (
                                    <div
                                        className="bg-gray-50 p-3 rounded-xl border border-gray-100 mb-4 space-y-1.5 relative z-10">
                                        {customerName && (
                                            <div className="flex items-center gap-2">
                                                <User className="w-4 h-4 text-gray-400"/>
                                                <span className="font-bold text-sm text-gray-800">{customerName}</span>
                                            </div>
                                        )}
                                        {customerPhone && (
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2 text-gray-600">
                                                    <Phone className="w-4 h-4 text-gray-400"/>
                                                    <span className="font-medium text-sm"
                                                          dir="ltr">{countryCode} {customerPhone}</span>
                                                </div>
                                                <a
                                                    href={generateCustomerWhatsAppLink()}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    data-html2canvas-ignore="true"
                                                    onClick={handleWhatsAppClick}
                                                    className="no-print bg-green-500 hover:bg-green-600 text-white px-2 py-1.5 rounded-lg flex items-center gap-1.5 text-xs font-bold transition-colors shadow-sm"
                                                >
                                                    <MessageCircle className="w-3.5 h-3.5"/> {t.sendWhatsApp}
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="space-y-3 relative z-10">
                                    <div className="flex justify-between text-gray-600">
                                        <span>{baseMetal === 'Gold' ? `${selectedCarat}K ` : ''}{baseMetal === 'Gold' ? t.gold : t.silver} {t.ratePerBaseUnit} ({baseRateUnit})</span>
                                        <span
                                            className="font-semibold text-gray-900">{formatCurrency(activeRates.base)}</span>
                                    </div>
                                    <div className="flex justify-between text-gray-600">
                                        <span>{baseMetal === 'Gold' ? `${selectedCarat}K ` : ''}{baseMetal === 'Gold' ? t.gold : t.silver} {t.ratePerGram}</span>
                                        <span
                                            className="font-semibold text-gray-900">{formatCurrency(activeRates.gram)}</span>
                                    </div>

                                    <div className="border-t border-dashed border-gray-200 my-2 pt-2"></div>

                                    <div className="flex justify-between text-gray-600">
                                        <span>{t.baseWeight}</span>
                                        <span className="font-semibold text-gray-900">
                                        {parsedWeight || 0} {weightUnit === 'Gram' ? t.grams : weightUnit === 'Tola' ? t.tolas : t.ounces} ({formatGrams(weightInGrams)})
                                    </span>
                                    </div>
                                    <div className="flex justify-between text-gray-600">
                                        <span>{t.wastage} ({parsedWastage || 0}%)</span>
                                        <span
                                            className="font-semibold text-gray-900">+{formatGrams(wastageInGrams)}</span>
                                    </div>
                                    <div className="flex justify-between p-2 rounded-lg mt-1"
                                         style={{backgroundColor: 'rgba(212, 175, 55, 0.08)'}}>
                                        <span className="font-medium"
                                              style={{color: COLOR_CHARCOAL}}>{t.netWeight} ({baseMetal === 'Gold' ? `${selectedCarat}K` : t.silver})</span>
                                        <span className="font-bold"
                                              style={{color: COLOR_CHARCOAL}}>{formatGrams(netWeightInGrams)}</span>
                                    </div>

                                    <div className="border-t border-dashed border-gray-200 my-2 pt-2"></div>

                                    <div className="flex justify-between text-gray-600">
                                        <span>{t.estValue}</span>
                                        <span className="font-semibold text-gray-900">{formatCurrency(goldValue)}</span>
                                    </div>

                                    {!isGuest ? (
                                        <>
                                            {parsedMakingCost > 0 && (
                                                <div className="flex justify-between text-gray-600">
                                                    <span>{t.makingCharges}</span>
                                                    <span
                                                        className="font-semibold text-gray-900">+{formatCurrency(parsedMakingCost)}</span>
                                                </div>
                                            )}

                                            {parsedStonesCost > 0 && (
                                                <div className="flex justify-between text-gray-600">
                                                    <span>{t.stoneCharges}</span>
                                                    <span
                                                        className="font-semibold text-gray-900">+{formatCurrency(parsedStonesCost)}</span>
                                                </div>
                                            )}

                                            {parsedOtherCost > 0 && (
                                                <div className="flex justify-between text-gray-600">
                                                    <span>{t.otherCharges}</span>
                                                    <span
                                                        className="font-semibold text-gray-900">+{formatCurrency(parsedOtherCost)}</span>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        totalAdditionalCost > 0 && (
                                            <div className="flex justify-between text-gray-600">
                                                <span>{t.additionalCosts}</span>
                                                <span
                                                    className="font-semibold text-gray-900">+{formatCurrency(totalAdditionalCost)}</span>
                                            </div>
                                        )
                                    )}

                                    {parsedDiscount > 0 && (
                                        <div
                                            className="flex justify-between text-red-600 bg-red-50 p-2 rounded-lg mt-1 border border-red-100">
                                            <span className="font-medium">{t.discount}</span>
                                            <span className="font-bold">-{formatCurrency(parsedDiscount)}</span>
                                        </div>
                                    )}

                                    <div className="border-t-2 my-3 pt-3" style={{borderColor: COLOR_CHARCOAL}}>
                                        <div className="flex justify-between items-center">
                                            <span className="text-lg font-bold"
                                                  style={{color: COLOR_CHARCOAL}}>{t.totalPrice}</span>
                                            <span className="text-2xl font-black"
                                                  style={{color: COLOR_GOLD}}>{formatCurrency(finalTotal)}</span>
                                        </div>
                                    </div>
                                </div>

                                <p className="text-center text-[10px] text-gray-400 mt-5 mb-1 italic leading-tight relative z-10">
                                    {t.disclaimer} {baseMetal === 'Gold' ? `${selectedCarat}K` : t.silver}.
                                </p>
                                <p className="text-center text-[10px] text-gray-600 mb-2 font-semibold leading-tight relative z-10">
                                    {t.pdfDisclaimer}
                                </p>

                                <div className="border-t border-dashed border-gray-200 mt-4 pt-4 text-center z-10">
                                    <div className="p-3 rounded-lg mb-2"
                                         style={{backgroundColor: COLOR_CHARCOAL, borderColor: '#333'}}>
                                        <div
                                            className="flex items-center justify-center gap-1 mb-1 text-[15px] leading-tight"
                                            style={{color: COLOR_GOLD}}>
                                            {/*<Smartphone className="w-4 h-4 flex-shrink-0"/>*/}
                                            <span>{t.promoMessage.replace(/\\n/g, '\n')} </span>
                                        </div>
                                        <a  href="https://wa.me/+923400737675" target="_blank" rel="noopener noreferrer" dir={isRtl ? 'rtl' : 'ltr'} className="font-bold  text-sm tracking-wide" style={{color: COLOR_GOLD}}>
                                            +92 340 0737675
                                        </a>
                                    </div>
                                    {/*<div className="text-[10px] space-y-1 block font-bold text-gray-400">*/}
                                    {/*    {t.poweredBy}*/}
                                    {/*</div>*/}
                                </div>
                            </section>
                        </div>
                    </div>

                    <footer className="text-center p-4 mt-2 no-print">
                        <a href="https://wa.me/+923400737675" target="_blank" rel="noopener noreferrer"
                           className="inline-flex items-center justify-center gap-2 text-xs text-gray-500 hover:text-gray-800 transition-all font-semibold">
                            <span className="relative flex h-2.5 w-2.5">
                                <span
                                    className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
                                    style={{backgroundColor: COLOR_GOLD}}></span>
                                <span className="relative inline-flex rounded-full h-2.5 w-2.5"
                                      style={{backgroundColor: COLOR_GOLD}}></span>
                            </span>
                            {t.poweredBy}
                        </a>
                    </footer>

                </main>

                {isGuest ? (
                    <div
                        className="bg-white border-t border-gray-100 p-4 pb-6 absolute bottom-0 w-full no-print shadow-[0_-15px_30px_-15px_rgba(0,0,0,0.1)] flex flex-col gap-3">
                        <p className="text-xs text-center text-gray-500 font-medium px-2 leading-relaxed">
                            {t.guestUpsellText}
                        </p>
                        <button
                            type="button"
                            onClick={() => {
                                setIsGuest(false);
                                localStorage.removeItem('jeweller_desk_guest');
                            }}
                            className="w-full text-gray-900 font-bold py-3.5 px-6 rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 text-lg border-none cursor-pointer hover:opacity-90"
                            style={{backgroundColor: COLOR_GOLD}}
                        >
                            <FileText className="w-5 h-5"/>
                            {t.signInToUnlock}
                        </button>
                    </div>
                ) : (
                    <div
                        className="bg-white border-t border-gray-100 p-4 pb-6 absolute bottom-0 w-full no-print shadow-[0_-15px_30px_-15px_rgba(0,0,0,0.1)]">
                        <button
                            type="button"
                            onClick={generatePDF}
                            disabled={isGenerating}
                            className="w-full text-white font-bold py-4 px-6 rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 text-lg border-none cursor-pointer hover:opacity-90"
                            style={{backgroundColor: COLOR_CHARCOAL}}
                        >
                            <Download className="w-5 h-5" style={{color: COLOR_GOLD}}/>
                            {isGenerating ? t.processing : t.downloadReceipt}
                        </button>
                    </div>
                )}

                {showLogoutConfirm && (
                    <div
                        className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center p-4 z-50 no-print backdrop-blur-sm">
                        <div
                            className="bg-[#2A2A2A] border border-gray-700 rounded-2xl max-w-sm w-full p-6 shadow-2xl flex flex-col items-center text-center">
                            <div className="p-3 rounded-full mb-4 bg-gray-800">
                                <LogOut className="w-8 h-8 text-red-400"/>
                            </div>
                            <h3 className="text-lg font-bold text-white mb-6">{t.logoutConfirm}</h3>
                            <div className="flex gap-3 w-full">
                                <button onClick={() => setShowLogoutConfirm(false)}
                                        className="flex-1 py-3 border border-gray-600 text-gray-300 font-semibold rounded-xl text-sm hover:bg-gray-700 transition-colors">
                                    {t.no}
                                </button>
                                <button onClick={handleLogout}
                                        className="flex-1 py-3 bg-red-500 hover:bg-red-600 text-white font-bold rounded-xl text-sm shadow-md transition-colors">
                                    {t.yes}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );

}